import type { AxiosResponse } from 'axios'
import type { ProductCategory } from '@repo/types'
import type {
  Certificate,
  CertificateAdmin,
  CertificateStatus,
  Pagination,
  PaginationParams,
  ProvenanceData,
  ScanLog,
  SortParams,
  TransferCertificate,
} from 'types'

export enum CertificatesActions {
  GET_CERTIFICATES = 'GET_CERTIFICATES',
  SET_CERTIFICATES = 'SET_CERTIFICATES',

  GET_CERTIFICATE = 'GET_CERTIFICATE',
  SET_CERTIFICATE = 'SET_CERTIFICATE',

  CREATE_CERTIFICATE = 'CREATE_CERTIFICATE',
  TRANSFER_CERTIFICATE = 'TRANSFER_CERTIFICATE',
  EDIT_CERTIFICATE = 'EDIT_CERTIFICATE',
  CANCEL_TRANSFER_CERTIFICATE = 'CANCEL_TRANSFER_CERTIFICATE',

  GET_CERTIFICATE_PROVENANCE = 'GET_CERTIFICATE_PROVENANCE',
  SET_CERTIFICATE_PROVENANCE = 'SET_CERTIFICATE_PROVENANCE',

  GET_CERTIFICATE_SCANS = 'GET_CERTIFICATE_SCANS',
  SET_CERTIFICATE_SCANS = 'SET_CERTIFICATE_SCANS',
}

export type CertificatesFilter = {
  companyId?: string
  statuses?: CertificateStatus[]
  type?: ProductCategory
  query?: string
  productId?: string
  // VERA-914 / VRG-105 (Stage B) — Owners tab "Certificates" count → Certificates
  // tab deep link (`/certificates?filters=owner:isAnyOf:<ownerId>`).
  ownerId?: string
  /**
   * VRG-265 (scan-to-filter) — narrow to an explicit SET of certificates, which is what an
   * additive scan produces. `query` carries one ILIKE string (so one tag) and `productId` answers
   * a different question, so neither can express "these five items I just scanned".
   */
  certificateIds?: string[]
  include_archived?: boolean
}

export type Certificates = {
  data: CertificateAdmin[]
  //filter: CertificatesFilter | EmptyObject ~ FIX: PEN-417
} & Pagination

export type ProvenanceResponseData = {
  data: ProvenanceData[]
} & Pagination

export type ScansResponseData = {
  data: ScanLog[]
} & Pagination

export type GetCertificatesPayload = CertificatesFilter & PaginationParams

export type GetCertificatesResponse = AxiosResponse<Certificates>

export type CreateCertificatePayload = {
  product_id: string
  tag_uids: string[]
  meta?: Record<any, any>
  description?: string
}

export type CreateCertificateResponse = AxiosResponse<Certificate>

export type GetCertificateByIdPayload = {
  certificateId: string
}

export type GetCertificateByIdResponse = AxiosResponse<CertificateAdmin>

export type TransferCertificatePayload = {
  certificateId: string
  toCustomerEmail: string
  price?: number
  currency?: string
}

export type EditCertificatePayload = {
  certificateId: string
  // Only tag_id was previously required by this payload; VERA-924 needs
  // single-field edits (e.g. manufactured_at only), so tag_id is optional now.
  tag_id?: string
  meta?: Record<any, any>
  description?: string
  // VERA-924: per-item production/manufacture date. String (MM/DD/YYYY, per
  // DatePickerField's onValueChange) or null to clear it.
  manufactured_at?: string | null
}

export type EditCertificateResponse = AxiosResponse<Certificate>

export type TransferCertificateResponse = AxiosResponse<TransferCertificate>

export type CancelTransferCertificatePayload = {
  certificateId: string
}

export type CancelTransferCertificateResponse = AxiosResponse<TransferCertificate>

export type GetCertificateProvenancePayload = {
  certificateId: string
} & PaginationParams &
  SortParams

export type GetCertificateProvenanceResponse = AxiosResponse<ProvenanceResponseData>

export type GetCertificateScansPayload = {
  certificateId: string
} & PaginationParams &
  SortParams

export type GetCertificateScansResponse = AxiosResponse<ScansResponseData>
